// src/components/Admin/UserProductCuration.js
import React, { useState, useEffect, useCallback } from 'react';

const UserProductCuration = () => {
  const [userProducts, setUserProducts] = useState([]);
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [activeTab, setActiveTab] = useState('pending'); // 'pending', 'approved', 'rejected', 'needs-affiliate'
  const [sortBy, setSortBy] = useState('newest');
  const [vendorFilter, setVendorFilter] = useState('');
  const [affiliateFilter, setAffiliateFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [previewItem, setPreviewItem] = useState(null);

  const affiliateStatuses = ['existing', 'pending-approval', 'not-available', 'needs-outreach'];
  const categories = ['Furniture', 'Lighting', 'Decor', 'Art', 'Books', 'Kitchen', 'Fashion', 'Electronics', 'Other'];

  // Mock user product data
  const mockUserProducts = [
    {
      id: 'up_001',
      title: 'Handwoven Kilim Rug - Vintage Style',
      description: 'Beautiful handwoven kilim rug with geometric patterns, perfect for modern interiors.',
      imageUrl: '/api/placeholder/400/400',
      productUrl: 'https://etsy.com/listing/vintage-kilim-rug',
      vendor: 'Etsy Shop: VintageRugCo',
      vendorDomain: 'etsy.com',
      price: 289.99,
      currency: 'USD',
      category: 'Decor',
      submittedBy: 'interior.lover',
      userEmail: 'interior@example.com',
      submittedAt: Date.now() - 86400000, // 1 day ago
      status: 'pending',
      affiliateStatus: 'existing', // Etsy has affiliate program
      affiliateProgram: 'Etsy Affiliate Program',
      commissionRate: 4.0,
      qualityScore: 0.87,
      aestheticScore: 0.92,
      priceCompetitive: true,
      tags: ['vintage', 'kilim', 'geometric', 'handwoven'],
      signals: ['artisanal', 'authentic', 'textural', 'global'],
      userReputation: 4.3,
      userSubmissions: 15,
      estimatedRevenue: 11.60 // 4% of $289.99
    },
    {
      id: 'up_002',
      title: 'Minimalist Oak Desk Organizer',
      description: 'Clean-lined desk organizer crafted from sustainable oak wood.',
      imageUrl: '/api/placeholder/400/400',
      productUrl: 'https://madetradesupply.com/oak-desk-organizer',
      vendor: 'Made Trade',
      vendorDomain: 'madetradesupply.com',
      price: 125.00,
      currency: 'USD',
      category: 'Office',
      submittedBy: 'design.enthusiast',
      userEmail: 'design@example.com',
      submittedAt: Date.now() - 172800000, // 2 days ago
      status: 'needs-affiliate',
      affiliateStatus: 'pending-approval',
      affiliateProgram: 'Made Trade Partner Program (Under Review)',
      commissionRate: null,
      qualityScore: 0.94,
      aestheticScore: 0.89,
      priceCompetitive: true,
      tags: ['minimalist', 'oak', 'sustainable', 'desk'],
      signals: ['minimal', 'functional', 'sustainable', 'crafted'],
      userReputation: 4.7,
      userSubmissions: 8,
      estimatedRevenue: null,
      affiliateNotes: 'Reached out to vendor 2 days ago, awaiting response'
    },
    {
      id: 'up_003',
      title: 'Ceramic Coffee Pour-Over Set',
      description: 'Japanese-inspired ceramic pour-over coffee set with wooden handle.',
      imageUrl: '/api/placeholder/400/400',
      productUrl: 'https://smallcoffeeshop.com/pour-over-set',
      vendor: 'Small Coffee Shop',
      vendorDomain: 'smallcoffeeshop.com',
      price: 78.50,
      currency: 'USD',
      category: 'Kitchen',
      submittedBy: 'coffee.curator',
      userEmail: 'coffee@example.com',
      submittedAt: Date.now() - 259200000, // 3 days ago
      status: 'rejected',
      affiliateStatus: 'not-available',
      affiliateProgram: 'No affiliate program available',
      commissionRate: null,
      qualityScore: 0.81,
      aestheticScore: 0.85,
      priceCompetitive: true,
      tags: ['ceramic', 'coffee', 'japanese', 'pourover'],
      signals: ['minimal', 'functional', 'ritual', 'crafted'],
      userReputation: 4.1,
      userSubmissions: 23,
      estimatedRevenue: null,
      rejectionReason: 'Vendor does not offer affiliate program',
      outreachRequested: true,
      outreachStatus: 'sent',
      outreachDate: Date.now() - 86400000
    },
    {
      id: 'up_004',
      title: 'Scandinavian Floor Lamp - Brass & Walnut',
      description: 'Mid-century inspired floor lamp with brass details and walnut base.',
      imageUrl: '/api/placeholder/400/400',
      productUrl: 'https://westelm.com/scandinavian-floor-lamp',
      vendor: 'West Elm',
      vendorDomain: 'westelm.com',
      price: 299.00,
      currency: 'USD',
      category: 'Lighting',
      submittedBy: 'light.seeker',
      userEmail: 'light@example.com',
      submittedAt: Date.now() - 345600000, // 4 days ago
      status: 'approved',
      affiliateStatus: 'existing',
      affiliateProgram: 'West Elm Affiliate Program',
      commissionRate: 3.5,
      qualityScore: 0.90,
      aestheticScore: 0.88,
      priceCompetitive: true,
      tags: ['scandinavian', 'brass', 'walnut', 'midcentury'],
      signals: ['warm', 'functional', 'statement', 'crafted'],
      userReputation: 4.5,
      userSubmissions: 31,
      estimatedRevenue: 10.47, // 3.5% of $299
      approvedAt: Date.now() - 172800000,
      approvedBy: 'admin',
      shopPlacement: 'user-profile', // or 'main-shop'
      clickCount: 24,
      conversionCount: 2
    }
  ];

  // Load user products
  const loadUserProducts = useCallback(async () => {
    setIsLoading(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 500));
      setUserProducts(mockUserProducts);
      setMessage(`✅ Loaded ${mockUserProducts.length} user product submissions`);
    } catch (error) {
      console.error('Error loading user products:', error);
      setMessage('❌ Error loading user product data');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUserProducts();
  }, [loadUserProducts]);

  // Filter and sort products
  const getFilteredProducts = useCallback(() => {
    let filtered = [...userProducts];
    
    // Filter by tab
    switch (activeTab) {
      case 'pending':
        filtered = filtered.filter(item => item.status === 'pending');
        break;
      case 'approved':
        filtered = filtered.filter(item => item.status === 'approved');
        break;
      case 'rejected':
        filtered = filtered.filter(item => item.status === 'rejected');
        break;
      case 'needs-affiliate':
        filtered = filtered.filter(item => item.status === 'needs-affiliate');
        break;
    }
    
    // Apply filters
    if (vendorFilter) {
      filtered = filtered.filter(item => 
        item.vendor.toLowerCase().includes(vendorFilter.toLowerCase()) ||
        item.vendorDomain.toLowerCase().includes(vendorFilter.toLowerCase())
      );
    }
    
    if (affiliateFilter) {
      filtered = filtered.filter(item => item.affiliateStatus === affiliateFilter);
    }
    
    if (categoryFilter) {
      filtered = filtered.filter(item => item.category === categoryFilter);
    }
    
    // Apply sorting
    const sorted = filtered.sort((a, b) => {
      switch (sortBy) {
        case 'newest':
          return (b.submittedAt || 0) - (a.submittedAt || 0);
        case 'oldest':
          return (a.submittedAt || 0) - (b.submittedAt || 0);
        case 'quality':
          return (b.qualityScore || 0) - (a.qualityScore || 0);
        case 'aesthetic':
          return (b.aestheticScore || 0) - (a.aestheticScore || 0);
        case 'revenue':
          return (b.estimatedRevenue || 0) - (a.estimatedRevenue || 0);
        case 'price-high':
          return (b.price || 0) - (a.price || 0);
        case 'price-low':
          return (a.price || 0) - (b.price || 0);
        default:
          return 0;
      }
    });
    
    return sorted;
  }, [userProducts, activeTab, vendorFilter, affiliateFilter, categoryFilter, sortBy]);

  const filteredProducts = getFilteredProducts();

  // Handle product actions
  const handleApproveItems = async (itemIds, placement = 'user-profile') => {
    if (itemIds.length === 0) {
      setMessage('❌ No items selected');
      return;
    }

    if (!window.confirm(`Approve ${itemIds.length} products for ${placement}?`)) {
      return;
    }

    setIsLoading(true);
    try {
      console.log('Approving products:', itemIds, 'Placement:', placement);
      setMessage(`✅ Would approve ${itemIds.length} products for ${placement} (backend API needed)`);
      setSelectedItems(new Set());
    } catch (error) {
      console.error('Error approving products:', error);
      setMessage('❌ Error approving products');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRejectItems = async (itemIds, reason = '') => {
    if (itemIds.length === 0) {
      setMessage('❌ No items selected');
      return;
    }

    const rejectionReason = reason || prompt('Reason for rejection:');
    if (rejectionReason === null) return;

    if (!window.confirm(`Reject ${itemIds.length} products?`)) {
      return;
    }

    setIsLoading(true);
    try {
      console.log('Rejecting products:', itemIds, 'Reason:', rejectionReason);
      setMessage(`✅ Would reject ${itemIds.length} products (backend API needed)`);
      setSelectedItems(new Set());
    } catch (error) {
      console.error('Error rejecting products:', error);
      setMessage('❌ Error rejecting products');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRequestAffiliate = async (itemIds) => {
    if (itemIds.length === 0) {
      setMessage('❌ No items selected');
      return;
    }

    setIsLoading(true);
    try {
      console.log('Requesting affiliate programs for:', itemIds);
      setMessage(`✅ Would request affiliate programs for ${itemIds.length} vendors (backend API needed)`);
      setSelectedItems(new Set());
    } catch (error) {
      console.error('Error requesting affiliate programs:', error);
      setMessage('❌ Error requesting affiliate programs');
    } finally {
      setIsLoading(false);
    }
  };

  const handleVendorOutreach = async (productId) => {
    if (!window.confirm('Send automated outreach email to vendor about affiliate program?')) {
      return;
    }

    setIsLoading(true);
    try {
      console.log('Sending vendor outreach for product:', productId);
      setMessage('✅ Would send vendor outreach email (backend API needed)');
    } catch (error) {
      console.error('Error sending vendor outreach:', error);
      setMessage('❌ Error sending vendor outreach');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle item selection
  const handleItemSelect = (itemId) => {
    const newSelected = new Set(selectedItems);
    if (newSelected.has(itemId)) {
      newSelected.delete(itemId);
    } else {
      newSelected.add(itemId);
    }
    setSelectedItems(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedItems.size === filteredProducts.length) {
      setSelectedItems(new Set());
    } else {
      setSelectedItems(new Set(filteredProducts.map(item => item.id)));
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return '#ffc107';
      case 'approved': return '#28a745';
      case 'rejected': return '#dc3545';
      case 'needs-affiliate': return '#17a2b8';
      default: return '#6c757d';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending': return '⏳';
      case 'approved': return '✅';
      case 'rejected': return '❌';
      case 'needs-affiliate': return '🔗';
      default: return '❓';
    }
  };

  const getAffiliateStatusColor = (status) => {
    switch (status) {
      case 'existing': return '#28a745';
      case 'pending-approval': return '#ffc107';
      case 'not-available': return '#dc3545';
      case 'needs-outreach': return '#17a2b8';
      default: return '#6c757d';
    }
  };

  const getScoreColor = (score) => {
    if (score >= 0.85) return '#28a745';
    if (score >= 0.7) return '#ffc107';
    return '#dc3545';
  };

  return (
    <div style={{
      maxWidth: '1400px',
      margin: '2rem auto',
      padding: '2rem',
      backgroundColor: 'white',
      borderRadius: '12px',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)'
    }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
        <div>
          <h2 style={{ margin: 0, color: '#333' }}>User Product Curation</h2>
          <p style={{ margin: '0.5rem 0 0', color: '#666', fontSize: '0.9rem' }}>
            Review user-submitted products and manage affiliate programs
          </p>
        </div>
        
        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
          <button
            onClick={() => window.location.href = '/admin/affiliate-approval'}
            style={{
              padding: '0.75rem 1.5rem',
              backgroundColor: '#17a2b8',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '0.9rem'
            }}
          >
            🔗 Manage Affiliates
          </button>
          
          <button
            onClick={() => window.location.href = '/admin'}
            style={{
              padding: '0.75rem 1.5rem',
              backgroundColor: '#6c757d',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '0.9rem'
            }}
          >
            ← Admin Dashboard
          </button>
          
          <button
            onClick={loadUserProducts}
            disabled={isLoading}
            style={{
              padding: '0.75rem 1.5rem',
              backgroundColor: '#28a745',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '0.9rem'
            }}
          >
            🔄 Refresh
          </button>
        </div>
      </div>

      {/* Quick Stats */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
        gap: '1rem',
        marginBottom: '2rem'
      }}>
        <div style={{
          padding: '1rem',
          backgroundColor: '#fff3cd',
          borderRadius: '8px',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#856404' }}>
            {userProducts.filter(p => p.status === 'pending').length}
          </div>
          <div style={{ fontSize: '0.8rem', color: '#666' }}>Pending Review</div>
        </div>

        <div style={{
          padding: '1rem',
          backgroundColor: '#cce5ff',
          borderRadius: '8px',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#004085' }}>
            {userProducts.filter(p => p.status === 'needs-affiliate').length}
          </div>
          <div style={{ fontSize: '0.8rem', color: '#666' }}>Need Affiliate</div>
        </div>

        <div style={{
          padding: '1rem',
          backgroundColor: '#d4edda',
          borderRadius: '8px',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#155724' }}>
            {userProducts.filter(p => p.status === 'approved').length}
          </div>
          <div style={{ fontSize: '0.8rem', color: '#666' }}>Approved</div>
        </div>

        <div style={{
          padding: '1rem',
          backgroundColor: '#f8d7da',
          borderRadius: '8px',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#721c24' }}>
            ${userProducts.filter(p => p.estimatedRevenue).reduce((sum, p) => sum + (p.estimatedRevenue || 0), 0).toFixed(2)}
          </div>
          <div style={{ fontSize: '0.8rem', color: '#666' }}>Est. Monthly Revenue</div>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ 
        display: 'flex', 
        borderBottom: '2px solid #e9ecef',
        marginBottom: '2rem'
      }}>
        {[
          { id: 'pending', label: 'Pending Review', count: userProducts.filter(p => p.status === 'pending').length },
          { id: 'needs-affiliate', label: 'Needs Affiliate', count: userProducts.filter(p => p.status === 'needs-affiliate').length },
          { id: 'approved', label: 'Approved', count: userProducts.filter(p => p.status === 'approved').length },
          { id: 'rejected', label: 'Rejected', count: userProducts.filter(p => p.status === 'rejected').length }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              padding: '1rem 2rem',
              backgroundColor: 'transparent',
              border: 'none',
              borderBottom: activeTab === tab.id ? '2px solid #007bff' : '2px solid transparent',
              color: activeTab === tab.id ? '#007bff' : '#666',
              cursor: 'pointer',
              fontSize: '1rem',
              fontWeight: '600'
            }}
          >
            {tab.label} ({tab.count})
          </button>
        ))}
      </div>

      {/* Filters */}
      <div style={{ 
        display: 'flex', 
        gap: '1rem', 
        marginBottom: '1.5rem', 
        padding: '1rem', 
        backgroundColor: '#f8f9fa', 
        borderRadius: '8px',
        flexWrap: 'wrap'
      }}>
        <div style={{ flex: 1, minWidth: '150px' }}>
          <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.9rem', fontWeight: '600' }}>
            Vendor Filter
          </label>
          <input
            type="text"
            value={vendorFilter}
            onChange={(e) => setVendorFilter(e.target.value)}
            placeholder="Vendor name or domain"
            style={{
              width: '100%',
              padding: '0.5rem',
              border: '1px solid #ddd',
              borderRadius: '4px',
              fontSize: '0.9rem'
            }}
          />
        </div>
        
        <div style={{ flex: 1, minWidth: '150px' }}>
          <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.9rem', fontWeight: '600' }}>
            Affiliate Status
          </label>
          <select
            value={affiliateFilter}
            onChange={(e) => setAffiliateFilter(e.target.value)}
            style={{
              width: '100%',
              padding: '0.5rem',
              border: '1px solid #ddd',
              borderRadius: '4px',
              fontSize: '0.9rem'
            }}
          >
            <option value="">All Statuses</option>
            <option value="existing">Existing Program</option>
            <option value="pending-approval">Pending Approval</option>
            <option value="not-available">Not Available</option>
            <option value="needs-outreach">Needs Outreach</option>
          </select>
        </div>
        
        <div style={{ flex: 1, minWidth: '150px' }}>
          <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.9rem', fontWeight: '600' }}>
            Category
          </label>
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            style={{
              width: '100%',
              padding: '0.5rem',
              border: '1px solid #ddd',
              borderRadius: '4px',
              fontSize: '0.9rem'
            }}
          >
            <option value="">All Categories</option>
            {categories.map(cat => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
        </div>
        
        <div style={{ flex: 1, minWidth: '150px' }}>
          <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.9rem', fontWeight: '600' }}>
            Sort By
          </label>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            style={{
              width: '100%',
              padding: '0.5rem',
              border: '1px solid #ddd',
              borderRadius: '4px',
              fontSize: '0.9rem'
            }}
          >
            <option value="newest">Newest First</option>
            <option value="oldest">Oldest First</option>
            <option value="quality">Quality Score</option>
            <option value="aesthetic">Aesthetic Score</option>
            <option value="revenue">Est. Revenue</option>
            <option value="price-high">Price High to Low</option>
            <option value="price-low">Price Low to High</option>
          </select>
        </div>
      </div>

      {/* Bulk Actions */}
      {(activeTab === 'pending' || activeTab === 'needs-affiliate') && (
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          marginBottom: '1rem',
          padding: '1rem',
          backgroundColor: '#e9ecef',
          borderRadius: '6px'
        }}>
          <div>
            <button
              onClick={handleSelectAll}
              style={{
                padding: '0.5rem 1rem',
                backgroundColor: '#6c757d',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '0.9rem',
                marginRight: '1rem'
              }}
            >
              {selectedItems.size === filteredProducts.length ? 'Deselect All' : 'Select All'}
            </button>
            
            <span style={{ fontSize: '0.9rem', color: '#666' }}>
              {selectedItems.size} of {filteredProducts.length} selected
            </span>
          </div>
          
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            {activeTab === 'pending' && (
              <>
                <button
                  onClick={() => handleApproveItems(Array.from(selectedItems), 'user-profile')}
                  disabled={selectedItems.size === 0 || isLoading}
                  style={{
                    padding: '0.5rem 1rem',
                    backgroundColor: selectedItems.size > 0 ? '#28a745' : '#ccc',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: selectedItems.size > 0 ? 'pointer' : 'not-allowed',
                    fontSize: '0.9rem'
                  }}
                >
                  ✅ Approve for User Shop ({selectedItems.size})
                </button>
                
                <button
                  onClick={() => handleApproveItems(Array.from(selectedItems), 'main-shop')}
                  disabled={selectedItems.size === 0 || isLoading}
                  style={{
                    padding: '0.5rem 1rem',
                    backgroundColor: selectedItems.size > 0 ? '#17a2b8' : '#ccc',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: selectedItems.size > 0 ? 'pointer' : 'not-allowed',
                    fontSize: '0.9rem'
                  }}
                >
                  ⭐ Promote to Main Shop ({selectedItems.size})
                </button>
                
                <button
                  onClick={() => handleRejectItems(Array.from(selectedItems))}
                  disabled={selectedItems.size === 0 || isLoading}
                  style={{
                    padding: '0.5rem 1rem',
                    backgroundColor: selectedItems.size > 0 ? '#dc3545' : '#ccc',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: selectedItems.size > 0 ? 'pointer' : 'not-allowed',
                    fontSize: '0.9rem'
                  }}
                >
                  ❌ Reject Selected ({selectedItems.size})
                </button>
              </>
            )}
            
            {activeTab === 'needs-affiliate' && (
              <button
                onClick={() => handleRequestAffiliate(Array.from(selectedItems))}
                disabled={selectedItems.size === 0 || isLoading}
                style={{
                  padding: '0.5rem 1rem',
                  backgroundColor: selectedItems.size > 0 ? '#17a2b8' : '#ccc',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: selectedItems.size > 0 ? 'pointer' : 'not-allowed',
                  fontSize: '0.9rem'
                }}
              >
                🔗 Request Affiliate Programs ({selectedItems.size})
              </button>
            )}
          </div>
        </div>
      )}

      {/* Products Grid */}
      {isLoading ? (
        <div style={{ textAlign: 'center', padding: '2rem', fontSize: '1.1rem', color: '#666' }}>
          Loading user products...
        </div>
      ) : (
        <div>
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fill, minmax(400px, 1fr))', 
            gap: '1.5rem' 
          }}>
            {filteredProducts.map((product) => (
              <div key={product.id} style={{
                border: product.affiliateStatus === 'not-available' ? '2px solid #dc3545' : '1px solid #ddd',
                borderRadius: '8px',
                padding: '1.5rem',
                backgroundColor: product.affiliateStatus === 'not-available' ? '#fff5f5' : 'white',
                position: 'relative',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
              }}>
                {/* Selection checkbox for pending and needs-affiliate */}
                {(activeTab === 'pending' || activeTab === 'needs-affiliate') && (
                  <input
                    type="checkbox"
                    checked={selectedItems.has(product.id)}
                    onChange={() => handleItemSelect(product.id)}
                    style={{
                      position: 'absolute',
                      top: '1rem',
                      left: '1rem',
                      transform: 'scale(1.2)',
                      zIndex: 1
                    }}
                  />
                )}

                {/* Status and Quality indicators */}
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center', 
                  marginBottom: '1rem',
                  marginTop: (activeTab === 'pending' || activeTab === 'needs-affiliate') ? '2rem' : '0'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span style={{ fontSize: '1.2rem' }}>
                      {getStatusIcon(product.status)}
                    </span>
                    <span style={{
                      backgroundColor: getStatusColor(product.status),
                      color: 'white',
                      padding: '0.25rem 0.5rem',
                      borderRadius: '12px',
                      fontSize: '0.75rem',
                      fontWeight: 'bold'
                    }}>
                      {product.status.toUpperCase().replace('-', ' ')}
                    </span>
                  </div>
                  
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                    <span style={{
                      backgroundColor: getScoreColor(product.qualityScore),
                      color: 'white',
                      padding: '0.25rem 0.5rem',
                      borderRadius: '12px',
                      fontSize: '0.7rem',
                      fontWeight: 'bold'
                    }}>
                      Q: {(product.qualityScore * 100).toFixed(0)}%
                    </span>
                    
                    <span style={{
                      backgroundColor: getScoreColor(product.aestheticScore),
                      color: 'white',
                      padding: '0.25rem 0.5rem',
                      borderRadius: '12px',
                      fontSize: '0.7rem',
                      fontWeight: 'bold'
                    }}>
                      A: {(product.aestheticScore * 100).toFixed(0)}%
                    </span>
                    
                    <button
                      onClick={() => setPreviewItem(product)}
                      style={{
                        padding: '0.25rem 0.5rem',
                        backgroundColor: '#17a2b8',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '0.7rem'
                      }}
                    >
                      👁️ Preview
                    </button>
                  </div>
                </div>

                {/* Product image */}
                <div style={{ marginBottom: '1rem' }}>
                  <img 
                    src={product.imageUrl} 
                    alt={product.title}
                    style={{
                      width: '100%',
                      height: '200px',
                      objectFit: 'cover',
                      borderRadius: '6px',
                      backgroundColor: '#f8f9fa'
                    }}
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                </div>
                
                {/* Product details */}
                <div style={{ marginBottom: '1rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                    <span style={{
                      backgroundColor: '#e9ecef',
                      color: '#495057',
                      padding: '0.25rem 0.5rem',
                      borderRadius: '12px',
                      fontSize: '0.75rem',
                      fontWeight: 'bold'
                    }}>
                      {product.category}
                    </span>
                    
                    <span style={{ fontSize: '1rem', fontWeight: '600', color: '#28a745' }}>
                      ${product.price} {product.currency}
                    </span>
                  </div>
                  
                  <h4 style={{ 
                    margin: '0 0 0.5rem 0', 
                    fontSize: '1rem', 
                    fontWeight: '600',
                    lineHeight: '1.3'
                  }}>
                    {product.title}
                  </h4>
                  
                  <p style={{ 
                    margin: '0 0 0.75rem 0', 
                    fontSize: '0.9rem', 
                    color: '#666',
                    lineHeight: '1.4',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden'
                  }}>
                    {product.description}
                  </p>
                  
                  <div style={{ fontSize: '0.8rem', color: '#888', marginBottom: '0.5rem' }}>
                    <strong>Vendor:</strong> {product.vendor}
                  </div>
                  
                  <div style={{ fontSize: '0.8rem', color: '#888', marginBottom: '0.5rem' }}>
                    <strong>Submitted by:</strong> {product.submittedBy} • Rep: {product.userReputation}/5.0
                  </div>
                  
                  {/* Affiliate status */}
                  <div style={{ 
                    marginTop: '0.75rem',
                    padding: '0.5rem',
                    backgroundColor: product.affiliateStatus === 'existing' ? '#e8f5e8' : 
                                   product.affiliateStatus === 'pending-approval' ? '#fff3cd' :
                                   product.affiliateStatus === 'not-available' ? '#fee' : '#cce5ff',
                    borderRadius: '4px',
                    fontSize: '0.8rem'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{
                        backgroundColor: getAffiliateStatusColor(product.affiliateStatus),
                        color: 'white',
                        padding: '0.25rem 0.5rem',
                        borderRadius: '12px',
                        fontSize: '0.7rem',
                        fontWeight: 'bold'
                      }}>
                        {product.affiliateStatus.toUpperCase().replace('-', ' ')}
                      </span>
                      
                      {product.commissionRate && (
                        <span style={{ fontWeight: '600', color: '#28a745' }}>
                          {product.commissionRate}% commission
                        </span>
                      )}
                    </div>
                    
                    <div style={{ marginTop: '0.25rem', color: '#666' }}>
                      <strong>Program:</strong> {product.affiliateProgram}
                    </div>
                    
                    {product.estimatedRevenue && (
                      <div style={{ marginTop: '0.25rem', color: '#28a745', fontWeight: '600' }}>
                        Est. Revenue: ${product.estimatedRevenue.toFixed(2)}/month
                      </div>
                    )}
                    
                    {product.affiliateNotes && (
                      <div style={{ marginTop: '0.25rem', color: '#666', fontSize: '0.75rem' }}>
                        <strong>Notes:</strong> {product.affiliateNotes}
                      </div>
                    )}
                  </div>
                  
                  {/* Performance metrics for approved products */}
                  {product.status === 'approved' && product.clickCount !== undefined && (
                    <div style={{ 
                      marginTop: '0.75rem',
                      padding: '0.5rem',
                      backgroundColor: '#e8f5e8',
                      borderRadius: '4px',
                      fontSize: '0.8rem'
                    }}>
                      <strong>Performance:</strong> {product.clickCount} clicks • {product.conversionCount} conversions
                      <div style={{ color: '#666', fontSize: '0.75rem' }}>
                        Conversion Rate: {product.clickCount > 0 ? ((product.conversionCount / product.clickCount) * 100).toFixed(2) : 0}%
                      </div>
                    </div>
                  )}
                  
                  {/* Rejection/Outreach info for rejected products */}
                  {product.status === 'rejected' && (
                    <div style={{ 
                      marginTop: '0.75rem',
                      padding: '0.5rem',
                      backgroundColor: '#fee',
                      borderRadius: '4px',
                      fontSize: '0.8rem'
                    }}>
                      <strong style={{ color: '#dc3545' }}>Rejection Reason:</strong>
                      <div style={{ marginTop: '0.25rem', color: '#666' }}>
                        {product.rejectionReason}
                      </div>
                      
                      {product.outreachRequested && (
                        <div style={{ marginTop: '0.5rem' }}>
                          <strong>Vendor Outreach:</strong> {product.outreachStatus === 'sent' ? '✅ Sent' : '⏳ Pending'}
                          {product.outreachDate && (
                            <div style={{ fontSize: '0.75rem', color: '#666' }}>
                              Sent: {new Date(product.outreachDate).toLocaleDateString()}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                  
                  {product.tags && product.tags.length > 0 && (
                    <div style={{ fontSize: '0.75rem', color: '#999', marginTop: '0.75rem' }}>
                      <strong>Tags:</strong> {product.tags.slice(0, 4).join(', ')}
                      {product.tags.length > 4 && ' ...'}
                    </div>
                  )}
                  
                  <div style={{ fontSize: '0.7rem', color: '#aaa', marginTop: '0.75rem' }}>
                    Submitted: {new Date(product.submittedAt).toLocaleDateString()}
                    {product.approvedAt && (
                      <span> • Approved: {new Date(product.approvedAt).toLocaleDateString()}</span>
                    )}
                  </div>
                </div>
                
                {/* Action buttons */}
                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                  {product.productUrl && (
                    <button
                      onClick={() => window.open(product.productUrl, '_blank')}
                      style={{
                        flex: 1,
                        padding: '0.5rem',
                        backgroundColor: '#17a2b8',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '0.8rem'
                      }}
                    >
                      🔗 View Product
                    </button>
                  )}
                  
                  {activeTab === 'pending' && (
                    <>
                      <button
                        onClick={() => handleApproveItems([product.id], 'user-profile')}
                        disabled={isLoading}
                        style={{
                          flex: 1,
                          padding: '0.5rem',
                          backgroundColor: '#28a745',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '0.8rem'
                        }}
                      >
                        ✅ User Shop
                      </button>
                      
                      <button
                        onClick={() => handleApproveItems([product.id], 'main-shop')}
                        disabled={isLoading}
                        style={{
                          padding: '0.5rem',
                          backgroundColor: '#0d47a1',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '0.8rem'
                        }}
                      >
                        ⭐ Main
                      </button>
                      
                      <button
                        onClick={() => handleRejectItems([product.id])}
                        disabled={isLoading}
                        style={{
                          padding: '0.5rem',
                          backgroundColor: '#dc3545',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '0.8rem'
                        }}
                      >
                        ❌
                      </button>
                    </>
                  )}
                  
                  {activeTab === 'needs-affiliate' && (
                    <button
                      onClick={() => handleRequestAffiliate([product.id])}
                      disabled={isLoading}
                      style={{
                        flex: 1,
                        padding: '0.5rem',
                        backgroundColor: '#17a2b8',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '0.8rem'
                      }}
                    >
                      🔗 Request Affiliate
                    </button>
                  )}
                  
                  {product.status === 'rejected' && product.affiliateStatus === 'not-available' && !product.outreachRequested && (
                    <button
                      onClick={() => handleVendorOutreach(product.id)}
                      disabled={isLoading}
                      style={{
                        flex: 1,
                        padding: '0.5rem',
                        backgroundColor: '#ffc107',
                        color: '#000',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '0.8rem'
                      }}
                    >
                      📧 Contact Vendor
                    </button>
                  )}
                  
                  <button
                    onClick={() => window.open(`mailto:${product.userEmail}`, '_blank')}
                    style={{
                      padding: '0.5rem',
                      backgroundColor: '#6c757d',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '0.8rem'
                    }}
                  >
                    📧
                  </button>
                </div>
              </div>
            ))}
          </div>
          
          {filteredProducts.length === 0 && (
            <div style={{ 
              textAlign: 'center', 
              padding: '3rem', 
              color: '#666',
              backgroundColor: '#f8f9fa',
              borderRadius: '8px'
            }}>
              <h3 style={{ margin: '0 0 0.5rem 0' }}>
                {activeTab === 'pending' && 'No pending products'}
                {activeTab === 'needs-affiliate' && 'No products need affiliate programs'}
                {activeTab === 'approved' && 'No approved products'}
                {activeTab === 'rejected' && 'No rejected products'}
              </h3>
              <p style={{ margin: 0 }}>
                {activeTab === 'pending' 
                  ? 'No user product submissions awaiting review' 
                  : activeTab === 'needs-affiliate'
                    ? 'No products requiring affiliate program setup'
                    : `No ${activeTab} products yet`}
              </p>
            </div>
          )}
        </div>
      )}

      {/* Product Preview Modal */}
      {previewItem && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '2rem',
            maxWidth: '900px',
            maxHeight: '61.2vh',
            overflow: 'auto',
            position: 'relative'
          }}>
            <button
              onClick={() => setPreviewItem(null)}
              style={{
                position: 'absolute',
                top: '1rem',
                right: '1rem',
                backgroundColor: 'transparent',
                border: 'none',
                fontSize: '1.5rem',
                cursor: 'pointer',
                color: '#666'
              }}
            >
              ✕
            </button>
            
            <h3 style={{ margin: '0 0 1rem 0', color: '#333' }}>Product Preview</h3>
            
            {/* Preview content would go here - similar to above but formatted for modal */}
            <div style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>
              Product preview modal content would be implemented here
            </div>
          </div>
        </div>
      )}

      {/* Status Message */}
      {message && (
        <div style={{
          marginTop: '1rem',
          padding: '0.75rem',
          backgroundColor: message.includes('❌') ? '#fee' : '#efe',
          color: message.includes('❌') ? '#c33' : '#363',
          borderRadius: '6px',
          fontSize: '0.9rem'
        }}>
          {message}
        </div>
      )}

      {/* Note about backend API needed */}
      <div style={{
        marginTop: '2rem',
        padding: '1rem',
        backgroundColor: '#fff3cd',
        border: '1px solid #ffeaa7',
        borderRadius: '6px',
        fontSize: '0.85rem',
        color: '#856404'
      }}>
        <strong>Note:</strong> This interface shows the user product curation workflow. You'll need a backend API to actually manage product approval, affiliate program integration, vendor outreach, and revenue tracking. The approve/reject/request actions currently just log what would happen.
      </div>
    </div>
  );
};

export default UserProductCuration;